import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showDeleteConfirmation, showItemExistsError,showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig'; // Import the new error alert function

interface FormErrors {
  brandName: string;
  selectedBrand: string;
  modelName: string;
  selectedModel: string;
}

interface VehicleBrand {
  id: number;
  name: string;
  models: { id: number; name: string }[];
}

interface BrandData {
  status: string;
  data: {
    vehicleBrands: VehicleBrand[];
  };
  statusDescription?: string;
}

const AddBrand: React.FC = () => {
  const [brandData, setBrandData] = useState<BrandData | null>(null);
  const [isBrandLoading, setIsBrandLoading] = useState<boolean>(true);
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const [selectedModelId, setSelectedModelId] = useState<string>("");
  const [newBrandName, setNewBrandName] = useState<string>("");
  const [newModelName, setNewModelName] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrors>({
    brandName: "",
    selectedBrand: "",
    modelName: "",
    selectedModel: "",
  });
  const [openFail, setOpenFail] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Operation Failed");

  const handleCloseFail = () => setOpenFail(false);

  // Fetch vehicle brands on component mount
  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = () => {
    setIsBrandLoading(true);
    apiDefinitions.getVehicleBrandModels("1", {})
      .then((response: { data: BrandData }) => {
        if (response.data.status === "S1000" && response.data.data?.vehicleBrands) {
          setBrandData(response.data);
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load brands");
          setOpenFail(true);
        }
        setIsBrandLoading(false);
      })
      .catch((error: any) => {
        setErrorMessage(error.response?.data?.statusDescription || "An error occurred");
        setOpenFail(true);
        setIsBrandLoading(false);
      });
  };

  const validateBrandForm = (name: string): boolean => {
    const errors: FormErrors = { brandName: "", selectedBrand: "", modelName: "", selectedModel: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.brandName = "Brand name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedBrand = (): boolean => {
    const errors: FormErrors = { brandName: "", selectedBrand: "", modelName: "", selectedModel: "" };
    let isValid = true;

    if (!selectedBrandId) {
      errors.selectedBrand = "Please select a brand";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateModelForm = (name: string): boolean => {
    const errors: FormErrors = { brandName: "", selectedBrand: "", modelName: "", selectedModel: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.modelName = "Model name is required";
      isValid = false;
    }

    if (!selectedBrandId) {
      errors.selectedBrand = "Please select a brand first";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedModel = (): boolean => {
    const errors: FormErrors = { brandName: "", selectedBrand: "", modelName: "", selectedModel: "" };
    let isValid = true;

    if (!selectedModelId) {
      errors.selectedModel = "Please select a model";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddBrandSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateBrandForm(newBrandName)) return;

    apiDefinitions.addVehicleBrand({ name: newBrandName, models: [] })
      .then((response: { data: BrandData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Brand created successfully");
          setNewBrandName("");
          fetchBrands();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError('A brand with this name already exists.');
        } else {
          setErrorMessage(response.data.statusDescription || "Brand creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A brand with this name already exists.");
        } else {
          setErrorMessage(error.response?.data?.statusDescription || "An error occurred");
          setOpenFail(true);
        }
      });
  };

  const handleDeleteBrand = () => {
    if (!validateSelectedBrand()) return;

    showDeleteConfirmation(() => {
      apiDefinitions.deleteVehicleBrand({ id: parseInt(selectedBrandId) })
        .then((response: { data: BrandData }) => {
          if (response.data.status === "S1000") {
            setSelectedBrandId("");
            setSelectedModelId("");
            fetchBrands();
          } else {
            showItemAlreadyUsed("Sorry, this brand is already in use and cannot be deleted.");
          }
        })
        .catch((error: any) => {
          showItemAlreadyUsed("Sorry, this brand is already in use and cannot be deleted.");
        });
    });
  };

  const handleAddModelSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateModelForm(newModelName)) return;

    apiDefinitions.addVehicleModel({ brandId: parseInt(selectedBrandId), modelName: newModelName })
      .then((response: { data: BrandData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Model created successfully");
          setNewModelName("");
          fetchBrands();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A model with this name already exists for the selected brand.");
        } else {
          setErrorMessage(response.data.statusDescription || "Model creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A model with this name already exists for the selected brand.");
        } else {
          setErrorMessage(error.response?.data?.statusDescription || "An error occurred");
          setOpenFail(true);
        }
      });
  };

  const handleDeleteModel = () => {
    if (!validateSelectedModel()) return;

    showDeleteConfirmation(() => {
      apiDefinitions.deleteVehicleModel({ id: parseInt(selectedModelId) })
        .then((response: { data: BrandData }) => {
          if (response.data.status === "S1000") {
            setSelectedModelId("");
            fetchBrands();
          } else {
            showItemAlreadyUsed("Sorry, this model is already in use and cannot be deleted.");
          }
        })
        .catch((error: any) => {
          showItemAlreadyUsed("Sorry, this model is already in use and cannot be deleted.");
        });
    });
  };

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBrandId(e.target.value);
    setSelectedModelId("");
    setFormErrors((prev) => ({ ...prev, selectedBrand: "", selectedModel: "" }));
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModelId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedModel: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Brand & Model</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Brand</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        placeholder="Enter brand name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#brand-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Brand
                      </button>
                    </div>
                    {formErrors.brandName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.brandName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedBrandId}
                      onChange={handleBrandChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Brand</option>
                      {isBrandLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        brandData?.data.vehicleBrands?.map((brand) => (
                          <option key={brand.id} value={brand.id}>{brand.name}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedBrand && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedBrand}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteBrand}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                {selectedBrandId && (
                  <div className="col-md-5">
                    <div className="mb-4">
                      <label className="col-form-label mb-2"><h5>Model</h5></label>
                      <div className="d-flex align-items-center">
                        <input
                          type="text"
                          className="form-control"
                          style={{ width: '300px', padding: '10px 15px' }}
                          value={newModelName}
                          onChange={(e) => setNewModelName(e.target.value)}
                          placeholder="Enter model name"
                        />
                        <button
                          type="button"
                          className="btn btn-success ms-3"
                          data-bs-toggle="modal"
                          data-bs-target="#add-model"
                          style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                        >
                          Add Model
                        </button>
                      </div>
                      {formErrors.modelName && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.modelName}</div>
                      )}
                      <select
                        className="form-select mt-3"
                        value={selectedModelId}
                        onChange={handleModelChange}
                        style={{ width: '300px', padding: '10px' }}
                      >
                        <option value="">Select Model</option>
                        {brandData?.data.vehicleBrands
                          .find((b) => b.id === parseInt(selectedBrandId))
                          ?.models?.map((model) => (
                            <option key={model.id} value={model.id}>{model.name}</option>
                          )) || <option disabled>No models available</option>}
                      </select>
                      {formErrors.selectedModel && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedModel}</div>
                      )}
                      <div className="d-flex gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleDeleteModel}
                          style={{ padding: '8px 16px' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Brand Modal */}
      <div className="modal fade" id="brand-modal" tabIndex={-1} aria-labelledby="add-brand" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-brand">Add New Brand</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddBrandSubmit}>
                <div className="mb-3">
                  <label htmlFor="newBrandName" className="form-label">Brand Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newBrandName"
                    value={newBrandName}
                    onChange={(e) => setNewBrandName(e.target.value)}
                    placeholder="Enter brand name (e.g., Toyota)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.brandName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.brandName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Brand
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Add Model Modal */}
      <div className="modal fade" id="add-model" tabIndex={-1} aria-labelledby="add-model" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-model">Add New Model</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddModelSubmit}>
                <div className="mb-3">
                  <label htmlFor="newModelName" className="form-label">Model Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newModelName"
                    value={newModelName}
                    onChange={(e) => setNewModelName(e.target.value)}
                    placeholder="Enter model name (e.g., Camry)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.modelName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.modelName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Model
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>


    </>
  );
};

export default AddBrand;